import {tokens} from '@pleo-io/telescope'
import React, {FC} from 'react'
import styled, {keyframes} from 'styled-components'

import Image from '@helios/shared/components/image'
import PrismicLink, {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import {Section, BackgroundWrapper} from '@helios/shared/styles/grid'
import {Text, TextLink} from '@helios/shared/styles/typography'
import ImageSource from '@helios/shared/types/image'
import mq from '@helios/shared/utils/media-query'

interface Props {
    logos: ImageSource[]
    title?: string
    ctaLink: PrismicLinkProps
    ctaText: string
}

export const CustomerLogosWithCta: FC<Props> = ({logos, title, ctaLink, ctaText}) => {
    const isSmallScreen = useMediaQuery.max('sm')

    /* We have two states for the logos row :
     * 1/ A rotating state on small screen that needs a 2nd chunk of logos so they can slide into view as the first chunk slides out
     * 2/ A static one for larger screens that would receive a single chunk */
    const finalLogos = isSmallScreen ? [logos, logos].flat() : logos

    return (
        <BackgroundWrapper color="muted">
            <StyledSection>
                {title && (
                    <StyledTitleText center variant="title-base">
                        {title}
                    </StyledTitleText>
                )}
                <ScrollableContainer>
                    <LogosCtaWrapper>
                        {finalLogos.map((logo, i) => (
                            <LogoSpacer key={`${logo.url}-${i}`}>
                                <Logo image={logo} height={60} />
                            </LogoSpacer>
                        ))}

                        <StyledTextLink variant="paragraph-large" as={PrismicLink} {...ctaLink}>
                            {ctaText}
                        </StyledTextLink>
                    </LogosCtaWrapper>
                </ScrollableContainer>
            </StyledSection>
        </BackgroundWrapper>
    )
}

const StyledSection = styled(Section)`
    ${mq.max('sm')} {
        padding-right: 0;
        padding-left: 0;
    }
`

const StyledTitleText = styled(Text)`
    padding-bottom: ${tokens.spacing32};

    ${mq.max('sm')} {
        padding-right: ${tokens.spacing24};
        padding-left: ${tokens.spacing24};
    }
`

const ScrollableContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    overflow: hidden;
`

const rotate = keyframes`
    100% {
        transform: translateX(-50%);
    }
`

const LogosCtaWrapper = styled.div`
    display: grid;
    grid-auto-flow: column;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    min-width: min-content;

    ${mq.max('sm')} {
        width: 200vw; /* Make the wrapper twice the width of the viewport so each chunk of logos can take up 100vw */
        animation-name: ${rotate};
        animation-duration: 45s;
        animation-timing-function: linear;
        animation-iteration-count: infinite;

        :hover {
            animation-play-state: paused;
        }
    }
`

const LogoSpacer = styled.div`
    ${mq.max('sm')} {
        margin: 0 ${tokens.spacing28};
    }
`

const Logo = styled(Image)`
    width: 100px;
    min-width: 100px;
    height: 40px;
    object-fit: contain;

    ${mq.min('md')} {
        width: 100px;
        min-width: 100px;
        height: 60px;
    }
`

const StyledTextLink = styled(TextLink)`
    ${mq.max('sm')} {
        display: none;
    }
`
